import { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import { FaCalendarAlt } from "react-icons/fa";
import { FaCalendarCheck } from "react-icons/fa";
import { FaCheckCircle } from "react-icons/fa";
import { IoAlarm } from "react-icons/io5";
import { IoMdMail } from "react-icons/io";

import api from "../api/api";
import "../App.css";

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const res = await api.get("/meeting");

    const ev = res.data.map((m) => ({
      title: m.purpose,
      date: m.schedule_at,
      extendedProps: {
        meeting_with: m.meeting_with,
        status: m.status,
        schedule_at: m.schedule_at,
      },

      backgroundColor:
        m.status === "Completed"
          ? "#22c55e"
          : m.status === "Cancelled"
            ? "#ef4444"
            : "#f59e0b",

      borderColor: "transparent",
    }));

    setEvents(ev);
    setTotal(res.data.length);
  };

  return (
    <div className="min-h-screen -mt-10">
      {/* HEADER */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="bg-gradient-to-r from-[#030244] to-blue-700 text-white rounded-2xl p-6 shadow-xl">
          <div className="flex items-center gap-4">
            <FaCalendarAlt className="text-4xl -mt-2" />
            <div>
              <h2 className="text-4xl font-semibold">Meeting Calendar</h2>
              <p className="text-md mt-1 opacity-80">
                Track & manage all meetings visually
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
          <h3 className="text-gray-800 font-medium text-[28px]">
            Total Meetings
          </h3>
          <p className="text-4xl font-semibold text-[#030244] mt-2">{total}</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
          <h3 className="text-gray-800 font-medium text-[28px]">Completed</h3>
          <p className="text-4xl font-semibold text-green-600 mt-2">
            {events.filter((e) => e.backgroundColor === "#22c55e").length}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
          <h3 className="text-gray-800 font-medium text-[28px]">Pending</h3>
          <p className="text-4xl font-semibold text-yellow-600 mt-2">
            {events.filter((e) => e.backgroundColor === "#f59e0b").length}
          </p>
        </div>
      </div>

      {/* CALENDAR */}
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-xl p-8 mt-14 border border-gray-100">
        <FullCalendar
          plugins={[dayGridPlugin]}
          initialView="dayGridMonth"
          events={events}
          height="auto"
          dayMaxEvents={2}
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth",
          }}
          eventTimeFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }}
          eventClick={(info) => {
            setSelectedEvent(info.event);
          }}
          /* MODERN EVENT DESIGN */
          eventContent={(eventInfo) => (
            <div className="bg-gradient-to-r from-[#030244] to-[#075183] text-white rounded-md px-1 py-1 text-[15px] w-full">
              <div className="text-[15px]">
                {eventInfo.timeText}
              </div>
              <div className="font-medium truncate">
                {eventInfo.event.title}
              </div>
            </div>
          )}
        />
      </div>

      {/* MODAL UI */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[400px] rounded-xl shadow-2xl overflow-hidden">
            {/* HEADER */}
            <div className="bg-gradient-to-r from-[#030449] to-[#0c536e] text-white px-5 py-3 flex justify-between items-center">
              <h2 className="font-semibold text-xl">{selectedEvent.title}</h2>

              <button
                onClick={() => setSelectedEvent(null)}
                className="text-white text-xl"
              >
                ✕
              </button>
            </div>

            {/* BODY */}
            <div className="p-5 space-y-4 text-gray-800 font-medium">
              {/* DATE */}
              <p className="flex items-center gap-3">
                <FaCalendarCheck className="text-[#030244] text-xl" />
                <span>
                  {new Date(
                    selectedEvent.extendedProps.schedule_at,
                  ).toLocaleDateString()}
                </span>
              </p>

              {/* TIME */}
              <p className="flex items-center gap-3">
                <IoAlarm className="text-[#030244] text-xl" />
                <span>
                  {new Date(
                    selectedEvent.extendedProps.schedule_at,
                  ).toLocaleTimeString()}
                </span>
              </p>

              {/* EMAIL */}
              <p className="flex items-center gap-3">
                <IoMdMail className="text-[#030244] text-xl" />
                <span>{selectedEvent.extendedProps.meeting_with}</span>
              </p>

              {/* STATUS */}
              <p className="flex items-center gap-3">
                <FaCheckCircle className="text-[#030244] text-xl" />
                <span>
                  Status:{" "}
                  <span className="font-semibold">
                    {selectedEvent.extendedProps.status}
                  </span>
                </span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// import { useEffect,useState } from "react";
// import FullCalendar from "@fullcalendar/react";
// import dayGridPlugin from "@fullcalendar/daygrid";

// import api from "../api/api";

// export default function CalendarPage(){

//  const [events,setEvents]=useState([]);

//  useEffect(()=>{
//   load();
//  },[]);

//  const load = async()=>{

//   const res = await api.get("/meeting");

//   const ev = res.data.map(m=>({
//    title:m.purpose,
//    date:m.schedule_at
//   }));

//   setEvents(ev);

//  };

//  return(

//  <div className="min-h-screen">

//  <h2 className="text-xl mb-4">Meeting Calendar</h2>

//  <FullCalendar
//  plugins={[dayGridPlugin]}
//  initialView="dayGridMonth"
//  events={events}
//  />

//  </div>

//  );
// }
